/**
 * Augmentation type definitions referencing the albumentations library.
 * Each augmentation maps to an albumentations transform.
 */

import { tKey, type I18nKey } from "@/core/i18n";
import { ModelAugmentationTypeEnum } from "@repo/schema";
import type { TFunction } from "i18next";

export type AugmentationCategory = "image" | "bbox";

export interface AugmentationParam {
  key: string;
  /** Absent when the input carries no caption of its own. */
  label?: I18nKey<"model">;
  type: "number" | "range" | "boolean" | "select";
  min?: number;
  max?: number;
  step?: number;
  default: number | boolean | string;
  /** For range type: the key for the second value */
  rangeEndKey?: string;
  rangeEndDefault?: number;
  options?: { label: I18nKey<"model">; value: string }[];
  unit?: string;
}

export interface AugmentationDefinition {
  id: ModelAugmentationTypeEnum;
  /** Display name */
  name: I18nKey<"model">;
  category: AugmentationCategory;
  description: I18nKey<"model">;
  params: AugmentationParam[];
}

export interface AppliedAugmentation {
  id: string;
  type: ModelAugmentationTypeEnum;
  params: Record<string, number | boolean | string>;
  duplicateImage?: boolean;
}

/** Summary text for an applied augmentation */
export function getAugmentationSummary(
  def: AugmentationDefinition,
  params: Record<string, number | boolean | string>,
  t: TFunction<["model", "common"]>,
): string {
  const yesNo = (value: unknown) =>
    value ? t("model:augmentation.yes") : t("model:augmentation.no");
  switch (def.id) {
    case ModelAugmentationTypeEnum.FLIP:
      return t("model:augmentation.flip.summary", {
        horizontal: yesNo(params.horizontal),
        vertical: yesNo(params.vertical),
      });
    case ModelAugmentationTypeEnum.ROTATE90:
      return t("model:augmentation.rotate90.summary");
    case ModelAugmentationTypeEnum.CROP:
      return t("model:augmentation.crop.summary", {
        min: params.scale_min,
        max: params.scale_max,
      });
    case ModelAugmentationTypeEnum.ROTATION:
      return t("model:augmentation.rotation.summary", {
        min: params.min,
        max: params.max,
      });
    case ModelAugmentationTypeEnum.SHEAR:
      return t("model:augmentation.shear.summary", {
        horizontal: params.min,
        vertical: params.max,
      });
    case ModelAugmentationTypeEnum.GRAYSCALE:
      return t("model:augmentation.grayscale.summary");
    case ModelAugmentationTypeEnum.HUE:
      return t("model:augmentation.hue.summary", {
        min: params.hue_shift_limit_min,
        max: params.hue_shift_limit_max,
      });
    case ModelAugmentationTypeEnum.SATURATION:
      return t("model:augmentation.saturation.summary", {
        min: params.min,
        max: params.max,
      });
    case ModelAugmentationTypeEnum.BRIGHTNESS:
      return t("model:augmentation.brightness.summary", {
        min: params.brightness_limit_min,
        max: params.brightness_limit_max,
      });
    case ModelAugmentationTypeEnum.CONTRAST:
      return t("model:augmentation.contrast.summary", {
        min: params.contrast_limit_min,
        max: params.contrast_limit_max,
      });
    case ModelAugmentationTypeEnum.BLUR:
      return t("model:augmentation.blur.summary", { size: params.blur_limit });
    case ModelAugmentationTypeEnum.NOISE:
      return t("model:augmentation.noise.summary", {
        stdMin: params.std_min,
        stdMax: params.std_max,
        meanMin: params.mean_min,
        meanMax: params.mean_max,
      });
    case ModelAugmentationTypeEnum.CUTOUT:
      return t("model:augmentation.cutout.summary");
    case ModelAugmentationTypeEnum.MOSAIC:
      return t("model:augmentation.mosaic.summary", {
        rows: params.rows,
        cols: params.cols,
      });
    case ModelAugmentationTypeEnum.CLAHE:
      return t("model:augmentation.clahe.summary", {
        clip: params.clip_limit,
        grid: params.tile_grid_size,
      });
    case ModelAugmentationTypeEnum.SHARPEN:
      return t("model:augmentation.sharpen.summary", {
        alphaMin: params.alpha_min,
        alphaMax: params.alpha_max,
        lightnessMin: params.lightness_min,
        lightnessMax: params.lightness_max,
      });
    case ModelAugmentationTypeEnum.MOTION_BLUR:
      return t("model:augmentation.motionBlur.summary", {
        size: params.blur_limit,
      });
    case ModelAugmentationTypeEnum.MEDIAN_BLUR:
      return t("model:augmentation.medianBlur.summary", {
        size: params.blur_limit,
      });
    case ModelAugmentationTypeEnum.DOWNSCALE:
      return t("model:augmentation.downscale.summary", {
        min: params.scale_min,
        max: params.scale_max,
      });
    case ModelAugmentationTypeEnum.IMAGE_COMPRESSION:
      return t("model:augmentation.imageCompression.summary", {
        min: params.quality_lower,
        max: params.quality_upper,
      });
    case ModelAugmentationTypeEnum.PERSPECTIVE:
      return t("model:augmentation.perspective.summary", {
        scale: params.scale,
      });
    case ModelAugmentationTypeEnum.EQUALIZE:
      return t("model:augmentation.equalize.summary");
    case ModelAugmentationTypeEnum.POSTERIZE:
      return t("model:augmentation.posterize.summary", {
        bits: params.num_bits,
      });
    case ModelAugmentationTypeEnum.RGB_SHIFT:
      return t("model:augmentation.rgbShift.summary", {
        r: params.r_shift_limit,
        g: params.g_shift_limit,
        b: params.b_shift_limit,
      });
    default:
      return "";
  }
}

// ─── Image-Level Augmentations ───────────────────────────────────────────────

const probabilityParam: AugmentationParam = {
  key: "p",
  label: tKey("model:augmentation.probability"),
  type: "number",
  min: 0,
  max: 1,
  step: 0.05,
  default: 0.5,
};

const flip: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.FLIP,
  name: tKey("model:augmentation.flip.name"),
  category: "image",
  description: tKey("model:augmentation.flip.description"),
  params: [
    {
      key: "horizontal",
      label: tKey("model:augmentation.flip.params.horizontal"),
      type: "boolean",
      default: true,
    },
    {
      key: "vertical",
      label: tKey("model:augmentation.flip.params.vertical"),
      type: "boolean",
      default: false,
    },
    probabilityParam,
  ],
};

const rotate90: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.ROTATE90,
  name: tKey("model:augmentation.rotate90.name"),
  category: "image",
  description: tKey("model:augmentation.rotate90.description"),
  params: [probabilityParam],
};

const crop: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.CROP,
  name: tKey("model:augmentation.crop.name"),
  category: "image",
  description: tKey("model:augmentation.crop.description"),
  params: [
    {
      key: "scale_min",
      label: tKey("model:augmentation.crop.params.scale_min"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.08,
    },
    {
      key: "scale_max",
      label: tKey("model:augmentation.crop.params.scale_max"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.8,
    },
    probabilityParam,
  ],
};

const rotation: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.ROTATION,
  name: tKey("model:augmentation.rotation.name"),
  category: "image",
  description: tKey("model:augmentation.rotation.description"),
  params: [
    {
      key: "min",
      label: tKey("model:augmentation.rotation.params.min"),
      type: "number",
      min: -180,
      max: 0,
      step: 1,
      default: -7,
      unit: "°",
    },
    {
      key: "max",
      label: tKey("model:augmentation.rotation.params.max"),
      type: "number",
      min: 0,
      max: 180,
      step: 1,
      default: 7,
      unit: "°",
    },
    probabilityParam,
  ],
};

const shear: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.SHEAR,
  name: tKey("model:augmentation.shear.name"),
  category: "image",
  description: tKey("model:augmentation.shear.description"),
  params: [
    {
      key: "min",
      type: "number",
      min: 0,
      max: 45,
      step: 1,
      default: 10,
      unit: "°",
    },
    {
      key: "max",
      label: tKey("model:augmentation.shear.params.max"),
      type: "number",
      min: 0,
      max: 45,
      step: 1,
      default: 0,
      unit: "°",
    },
    probabilityParam
  ],
};

const grayscale: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.GRAYSCALE,
  name: tKey("model:augmentation.grayscale.name"),
  category: "image",
  description: tKey("model:augmentation.grayscale.description"),
  params: [probabilityParam],
};

const hue: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.HUE,
  name: tKey("model:augmentation.hue.name"),
  category: "image",
  description: tKey("model:augmentation.hue.description"),
  params: [
    {
      key: "hue_shift_limit_min",
      label: tKey("model:augmentation.hue.params.hue_shift_limit_min"),
      type: "number",
      min: -180,
      max: 180,
      step: 1,
      default: -20,
    },
    {
      key: "hue_shift_limit_max",
      label: tKey("model:augmentation.hue.params.hue_shift_limit_max"),
      type: "number",
      min: -180,
      max: 180,
      step: 1,
      default: 20,
    },
    probabilityParam,
  ],
};

const saturation: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.SATURATION,
  name: tKey("model:augmentation.saturation.name"),
  category: "image",
  description: tKey("model:augmentation.saturation.description"),
  params: [
    {
      key: "min",
      label: tKey("model:augmentation.saturation.params.min"),
      type: "number",
      min: -255,
      max: 255,
      step: 1,
      default: 30,
    },
    {
      key: "max",
      label: tKey("model:augmentation.saturation.params.max"),
      type: "number",
      min: -255,
      max: 255,
      step: 1,
      default: 30,
    },
    probabilityParam,
  ],
};

const brightness: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.BRIGHTNESS,
  name: tKey("model:augmentation.brightness.name"),
  category: "image",
  description: tKey("model:augmentation.brightness.description"),
  params: [
    {
      key: "brightness_limit_min",
      label: tKey("model:augmentation.brightness.params.brightness_limit_min"),
      type: "number",
      min: -1,
      max: 1,
      step: 0.05,
      default: -0.2,
    },
    {
      key: "brightness_limit_max",
      label: tKey("model:augmentation.brightness.params.brightness_limit_max"),
      type: "number",
      min: -1,
      max: 1,
      step: 0.05,
      default: 0.2,
    },
    probabilityParam,
  ],
};

const contrast: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.CONTRAST,
  name: tKey("model:augmentation.contrast.name"),
  category: "image",
  description: tKey("model:augmentation.contrast.description"),
  params: [
    {
      key: "contrast_limit_min",
      label: tKey("model:augmentation.contrast.params.contrast_limit_min"),
      type: "number",
      min: -1,
      max: 1,
      step: 0.05,
      default: -0.2,
    },
    {
      key: "contrast_limit_max",
      label: tKey("model:augmentation.contrast.params.contrast_limit_max"),
      type: "number",
      min: -1,
      max: 1,
      step: 0.05,
      default: 0.2,
    },
    probabilityParam,
  ],
};

const blur: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.BLUR,
  name: tKey("model:augmentation.blur.name"),
  category: "image",
  description: tKey("model:augmentation.blur.description"),
  params: [
    {
      key: "blur_limit",
      label: tKey("model:augmentation.blur.params.blur_limit"),
      type: "number",
      min: 3,
      max: 31,
      step: 2,
      default: 7,
    },
    probabilityParam,
  ],
};

const noise: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.NOISE,
  name: tKey("model:augmentation.noise.name"),
  category: "image",
  description: tKey("model:augmentation.noise.description"),
  params: [
    {
      key: "std_min",
      label: tKey("model:augmentation.noise.params.std_min"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.2,
    },
    {
      key: "std_max",
      label: tKey("model:augmentation.noise.params.std_max"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.44,
    },
    {
      key: "mean_min",
      label: tKey("model:augmentation.noise.params.mean_min"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0,
    },
    {
      key: "mean_max",
      label: tKey("model:augmentation.noise.params.mean_max"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0,
    },
    {
      key: "per_channel",
      label: tKey("model:augmentation.noise.params.per_channel"),
      type: "boolean",
      default: false,
    },
    probabilityParam
  ],
};

const cutout: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.CUTOUT,
  name: tKey("model:augmentation.cutout.name"),
  category: "image",
  description: tKey("model:augmentation.cutout.description"),
  params: [probabilityParam],
};

const mosaic: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.MOSAIC,
  name: tKey("model:augmentation.mosaic.name"),
  category: "image",
  description: tKey("model:augmentation.mosaic.description"),
  params: [
    {
      key: "rows",
      label: tKey("model:augmentation.mosaic.params.rows"),
      type: "number",
      min: 1,
      max: 100,
      step: 1,
      default: 2,
    },
    {
      key: "cols",
      label: tKey("model:augmentation.mosaic.params.cols"),
      type: "number",
      min: 1,
      max: 100,
      step: 1,
      default: 2,
    },
    probabilityParam,
  ],
};

const clahe: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.CLAHE,
  name: tKey("model:augmentation.clahe.name"),
  category: "image",
  description: tKey("model:augmentation.clahe.description"),
  params: [
    {
      key: "clip_limit",
      label: tKey("model:augmentation.clahe.params.clip_limit"),
      type: "number",
      min: 1,
      max: 20,
      step: 0.5,
      default: 4.0,
    },
    {
      key: "tile_grid_size",
      label: tKey("model:augmentation.clahe.params.tile_grid_size"),
      type: "number",
      min: 1,
      max: 16,
      step: 1,
      default: 8,
    },
    probabilityParam,
  ],
};

const sharpen: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.SHARPEN,
  name: tKey("model:augmentation.sharpen.name"),
  category: "image",
  description: tKey("model:augmentation.sharpen.description"),
  params: [
    {
      key: "alpha_min",
      label: tKey("model:augmentation.sharpen.params.alpha_min"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.2,
    },
    {
      key: "alpha_max",
      label: tKey("model:augmentation.sharpen.params.alpha_max"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.5,
    },
    {
      key: "lightness_min",
      label: tKey("model:augmentation.sharpen.params.lightness_min"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.5,
    },
    {
      key: "lightness_max",
      label: tKey("model:augmentation.sharpen.params.lightness_max"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 1.0,
    },
    probabilityParam,
  ],
};

const motionBlur: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.MOTION_BLUR,
  name: tKey("model:augmentation.motionBlur.name"),
  category: "image",
  description: tKey("model:augmentation.motionBlur.description"),
  params: [
    {
      key: "blur_limit",
      label: tKey("model:augmentation.motionBlur.params.blur_limit"),
      type: "number",
      min: 3,
      max: 31,
      step: 2,
      default: 7,
    },
    probabilityParam,
  ],
};

const medianBlur: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.MEDIAN_BLUR,
  name: tKey("model:augmentation.medianBlur.name"),
  category: "image",
  description: tKey("model:augmentation.medianBlur.description"),
  params: [
    {
      key: "blur_limit",
      label: tKey("model:augmentation.medianBlur.params.blur_limit"),
      type: "number",
      min: 3,
      max: 31,
      step: 2,
      default: 5,
    },
    probabilityParam,
  ],
};

const downscale: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.DOWNSCALE,
  name: tKey("model:augmentation.downscale.name"),
  category: "image",
  description: tKey("model:augmentation.downscale.description"),
  params: [
    {
      key: "scale_min",
      label: tKey("model:augmentation.downscale.params.scale_min"),
      type: "number",
      min: 0.1,
      max: 0.9,
      step: 0.05,
      default: 0.25,
    },
    {
      key: "scale_max",
      label: tKey("model:augmentation.downscale.params.scale_max"),
      type: "number",
      min: 0.1,
      max: 0.99,
      step: 0.01,
      default: 0.5,
    },
    probabilityParam,
  ],
};

const imageCompression: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.IMAGE_COMPRESSION,
  name: tKey("model:augmentation.imageCompression.name"),
  category: "image",
  description: tKey("model:augmentation.imageCompression.description"),
  params: [
    {
      key: "quality_lower",
      label: tKey("model:augmentation.imageCompression.params.quality_lower"),
      type: "number",
      min: 1,
      max: 100,
      step: 1,
      default: 50,
    },
    {
      key: "quality_upper",
      label: tKey("model:augmentation.imageCompression.params.quality_upper"),
      type: "number",
      min: 1,
      max: 100,
      step: 1,
      default: 95,
    },
    probabilityParam,
  ],
};

const perspective: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.PERSPECTIVE,
  name: tKey("model:augmentation.perspective.name"),
  category: "image",
  description: tKey("model:augmentation.perspective.description"),
  params: [
    {
      key: "scale",
      label: tKey("model:augmentation.perspective.params.scale"),
      type: "number",
      min: 0,
      max: 0.2,
      step: 0.005,
      default: 0.05,
    },
    probabilityParam,
  ],
};

const equalize: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.EQUALIZE,
  name: tKey("model:augmentation.equalize.name"),
  category: "image",
  description: tKey("model:augmentation.equalize.description"),
  params: [probabilityParam],
};

const posterize: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.POSTERIZE,
  name: tKey("model:augmentation.posterize.name"),
  category: "image",
  description: tKey("model:augmentation.posterize.description"),
  params: [
    {
      key: "num_bits",
      label: tKey("model:augmentation.posterize.params.num_bits"),
      type: "number",
      min: 1,
      max: 8,
      step: 1,
      default: 4,
    },
    probabilityParam,
  ],
};

const rgbShift: AugmentationDefinition = {
  id: ModelAugmentationTypeEnum.RGB_SHIFT,
  name: tKey("model:augmentation.rgbShift.name"),
  category: "image",
  description: tKey("model:augmentation.rgbShift.description"),
  params: [
    {
      key: "r_shift_limit",
      label: tKey("model:augmentation.rgbShift.params.r_shift_limit"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.2,
    },
    {
      key: "g_shift_limit",
      label: tKey("model:augmentation.rgbShift.params.g_shift_limit"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.2,
    },
    {
      key: "b_shift_limit",
      label: tKey("model:augmentation.rgbShift.params.b_shift_limit"),
      type: "number",
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.2,
    },
    probabilityParam,
  ],
};

export const IMAGE_AUGMENTATIONS: AugmentationDefinition[] = [
  flip,
  rotate90,
  crop,
  rotation,
  shear,
  grayscale,
  hue,
  saturation,
  brightness,
  contrast,
  blur,
  noise,
  cutout,
  mosaic,
  clahe,
  sharpen,
  motionBlur,
  medianBlur,
  downscale,
  imageCompression,
  perspective,
  equalize,
  posterize,
  rgbShift,
];

export const ALL_AUGMENTATIONS: AugmentationDefinition[] = [
  ...IMAGE_AUGMENTATIONS,
];

export function getAugmentationById(
  id: string,
): AugmentationDefinition | undefined {
  return ALL_AUGMENTATIONS.find((a) => a.id === id);
}
